'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { ArrowLeft, MessageCircle, Share2, Bell } from 'lucide-react';
import {
  getFixtureById,
  getStatusLabel,
  isInPlay,
  getFlag,
  type LiveFixture,
} from '@/lib/api-football';
import { subscribeToScoreUpdates, type ScoreUpdatePayload } from '@/lib/supabase-realtime';
import { useLanguage } from '@/contexts/LanguageContext';

export default function MatchDetailHero() {
  const searchParams = useSearchParams();
  const fixtureId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const { t } = useLanguage();

  const [match, setMatch] = useState<LiveFixture | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const matchRef = useRef<LiveFixture | null>(null);

  const fetchMatch = useCallback(async () => {
    if (!fixtureId) {
      setLoading(false);
      return;
    }
    try {
      const data = await getFixtureById(fixtureId);
      if (data) {
        setMatch(data);
        matchRef.current = data;
      }
    } catch {
      // leave match as null
    } finally {
      setLoading(false);
    }
  }, [fixtureId]);

  useEffect(() => {
    setMounted(true);
    fetchMatch();

    intervalRef.current = setInterval(async () => {
      if (matchRef.current && isInPlay(matchRef.current.fixture.status)) {
        fetchMatch();
      }
    }, 15_000);

    const unsub = subscribeToScoreUpdates((payload: ScoreUpdatePayload) => {
      setMatch(prev => {
        if (!prev || prev.fixture.id !== payload.fixtureId) return prev;
        const updated = {
          ...prev,
          goals: { home: payload.homeScore, away: payload.awayScore },
          fixture: {
            ...prev.fixture,
            status: { ...prev.fixture.status, elapsed: payload.minute },
          },
        };
        matchRef.current = updated;
        return updated;
      });
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (unsub) unsub();
    };
  }, [fetchMatch]);

  if (!mounted || loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <Link href="/live-games-dashboard" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
            <span>{t.gameDetail.allMatches}</span>
          </Link>
        </div>
        <div className="rounded-2xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 20%)', height: 280 }} />
      </div>
    );
  }

  if (!match) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <Link href="/live-games-dashboard" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
            <span>{t.gameDetail.allMatches}</span>
          </Link>
        </div>
        <div className="rounded-2xl p-12 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 20%)' }}>
          <div className="text-4xl mb-3">⚽</div>
          <p className="text-slate-300 font-semibold">{t.gameDetail.matchNotFound}</p>
          <p className="text-slate-500 text-sm mt-1">
            {fixtureId ? `Fixture #${fixtureId} ${t.gameDetail.fixtureNotLoaded}` : t.gameDetail.noFixtureId}
          </p>
          <Link href="/live-scores" className="inline-block mt-4 text-sm text-orange-400 hover:text-orange-300 transition-colors">
            {t.gameDetail.browseLiveScores}
          </Link>
        </div>
      </div>
    );
  }

  const { fixture, league, teams, goals } = match;
  const statusLabel = getStatusLabel(fixture.status);
  const inPlay = isInPlay(fixture.status);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Link href="/live-games-dashboard" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={16} />
          <span>{t.gameDetail.allMatches}</span>
        </Link>
        <div className="flex items-center gap-2">
          <button className="btn-ghost p-2 rounded-lg" aria-label="Set alert">
            <Bell size={16} />
          </button>
          <button className="btn-ghost p-2 rounded-lg" aria-label="Share match">
            <Share2 size={16} />
          </button>
          <Link href="/live-chat" className="flex items-center gap-2 btn-primary text-sm">
            <MessageCircle size={15} />
            <span>{t.gameDetail.liveChat}</span>
          </Link>
        </div>
      </div>

      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, hsl(222 35% 11%) 0%, hsl(222 28% 14%) 100%)',
          border: '1px solid hsl(222 25% 20%)',
        }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full opacity-10 blur-3xl" style={{ background: 'hsl(25 95% 53%)' }} />
        </div>

        <div className="relative px-6 py-8 lg:px-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="text-xs font-medium text-slate-500">{league.round}</span>
            {inPlay ? (
              <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: 'hsl(0 84% 60% / 0.15)', color: 'hsl(0 84% 70%)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
                LIVE {fixture.status.elapsed}&apos;
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: 'hsl(38 92% 50% / 0.15)', color: 'hsl(38 92% 65%)' }}>
                {statusLabel}
              </span>
            )}
            <span className="text-xs font-medium text-slate-500">{fixture.venue.name}{fixture.venue.city ? `, ${fixture.venue.city}` : ''}</span>
          </div>

          <div className="flex items-center justify-center gap-8 lg:gap-16">
            <div className="flex flex-col items-center gap-3">
              {teams.home.logo ? (
                <img src={teams.home.logo} alt={teams.home.name} className="w-16 h-16 object-contain" />
              ) : (
                <span className="text-6xl">{getFlag(teams.home.name)}</span>
              )}
              <span className="text-xl font-bold text-white">{teams.home.name}</span>
              <span className="text-sm text-slate-500">{teams.home.code ?? teams.home.name.slice(0, 3).toUpperCase()}</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-4">
                <span className="font-mono text-7xl lg:text-8xl font-black text-white tabular-nums">{goals.home ?? 0}</span>
                <span className="text-3xl text-slate-600 font-light">–</span>
                <span className="font-mono text-7xl lg:text-8xl font-black text-white tabular-nums">{goals.away ?? 0}</span>
              </div>
              <span className="text-xs text-slate-500 tracking-widest uppercase">{fixture.status.long} · {fixture.status.elapsed ?? 0}&apos;</span>
            </div>

            <div className="flex flex-col items-center gap-3">
              {teams.away.logo ? (
                <img src={teams.away.logo} alt={teams.away.name} className="w-16 h-16 object-contain" />
              ) : (
                <span className="text-6xl">{getFlag(teams.away.name)}</span>
              )}
              <span className="text-xl font-bold text-white">{teams.away.name}</span>
              <span className="text-sm text-slate-500">{teams.away.code ?? teams.away.name.slice(0, 3).toUpperCase()}</span>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-8 pt-6" style={{ borderTop: '1px solid hsl(222 25% 18%)' }}>
            {fixture.referee && (
              <div className="text-sm text-slate-400">
                <span className="text-slate-600">{t.gameDetail.referee}: </span>
                <span>{fixture.referee}</span>
              </div>
            )}
            <div className="text-sm text-slate-400">
              <span className="text-slate-600">{t.gameDetail.season}: </span>
              <span className="font-mono tabular-nums">{league.season}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}